import { useQuery } from '@tanstack/react-query';

/**
 * Tela de entrada (30/10): a logo e o fundo do login e da saída, configurados pela plataforma em Admin > Painel admin > Tela de
 * entrada. A tela pública lê `GET /api/v1/publico/tela-entrada` (sem sessão) e monta os endereços das imagens pelo nome que o
 * servidor gera (com a versão: cache imutável). Sem configuração, com a rota fora ou lenta, ou com a imagem falhando, valem o
 * farol e a logo da Malgo.
 */

export interface ImagemTelaEntrada {
  versao: string;
  largura: number;
  altura: number;
  atualizado_em?: string;
}
/** Lista de 30/10 (item 1): textos da faixa da marca; `null` = o padrão do idioma. */
export interface TextosTelaEntrada {
  kicker: string | null;
  titulo: string | null;
  subtitulo: string | null;
}
export interface TelaEntrada {
  fundo: ImagemTelaEntrada | null;
  logo: ImagemTelaEntrada | null;
  textos: TextosTelaEntrada;
}
export const TEXTOS_PADRAO: TextosTelaEntrada = { kicker: null, titulo: null, subtitulo: null };
export const LIMITES_TEXTOS = { kicker: 40, titulo: 120, subtitulo: 300 } as const;

export const LARGURAS_FUNDO = [768, 1280, 1920] as const;
const BASE = '/api/v1/publico/tela-entrada';
const VERSAO = /^[0-9a-f]{16}$/;

export const urlFundo = (versao: string, largura: (typeof LARGURAS_FUNDO)[number], ext: 'webp' | 'avif') => `${BASE}/fundo-${versao}-${largura}.${ext}`;
export const urlLogo = (versao: string, ext: 'png' | 'webp') => `${BASE}/logo-${versao}-logo.${ext}`;

/** `srcset` do fundo num formato (as três larguras). */
export const srcSetFundo = (versao: string, ext: 'webp' | 'avif') => LARGURAS_FUNDO.map((l) => `${urlFundo(versao, l, ext)} ${l}w`).join(', ');

/** Só aceita o que tem a forma certa (resposta estranha vale como "sem configuração"). */
export function lerTelaEntrada(bruto: unknown): TelaEntrada {
  const r = (bruto ?? {}) as Record<string, unknown>;
  const img = (v: unknown): ImagemTelaEntrada | null => {
    const o = v as Partial<ImagemTelaEntrada> | null;
    return o && typeof o.versao === 'string' && VERSAO.test(o.versao) ? { versao: o.versao, largura: Number(o.largura) || 0, altura: Number(o.altura) || 0, atualizado_em: o.atualizado_em } : null;
  };
  const tx = (r.textos ?? {}) as Record<string, unknown>;
  const texto = (v: unknown, max: number) => (typeof v === 'string' && v.trim() && v.length <= max ? v : null);
  return {
    fundo: img(r.fundo),
    logo: img(r.logo),
    textos: { kicker: texto(tx.kicker, LIMITES_TEXTOS.kicker), titulo: texto(tx.titulo, LIMITES_TEXTOS.titulo), subtitulo: texto(tx.subtitulo, LIMITES_TEXTOS.subtitulo) },
  };
}

/** Prazo da consulta pública: passou disso, a tela segue com o padrão (o farol não espera uma rota pendurada). */
export const PRAZO_TELA_ENTRADA_MS = 2500;

export async function buscarTelaEntrada(f: typeof fetch = fetch): Promise<TelaEntrada> {
  const controle = new AbortController();
  const id = setTimeout(() => controle.abort(), PRAZO_TELA_ENTRADA_MS);
  try {
    const r = await f(BASE, { credentials: 'omit', headers: { Accept: 'application/json' }, signal: controle.signal });
    if (!r.ok) throw new Error(`tela-entrada ${r.status}`);
    return lerTelaEntrada(await r.json());
  } finally {
    clearTimeout(id);
  }
}

export const CHAVE_TELA_ENTRADA = ['publico', 'tela-entrada'] as const;

/** A configuração para as páginas públicas; `undefined` enquanto carrega, o padrão (`null`s) em erro. */
export function useTelaEntrada(): TelaEntrada | undefined {
  const q = useQuery({ queryKey: CHAVE_TELA_ENTRADA, queryFn: () => buscarTelaEntrada(), staleTime: 60_000, retry: false, refetchOnWindowFocus: false });
  if (q.isError) return { fundo: null, logo: null, textos: TEXTOS_PADRAO };
  return q.data;
}
